<template>
  <div class="call-target-menu">
    <BLayout class="call-target-menu-layout">
      <div
        v-if="activeCti === 'miitel'"
        class="miitel-contents"
      >
        <MiitelWidget
          v-if="recreateState"
          ref="miitelWidget"
          :company-id="miitelCompanyId"
          :access-key="miitelAccessKey"
        />
      </div>
      <ZoomPhoneWidget
        v-if="activeCti === 'zoom'"
      />
      <div class="call-target-menu-contents">
        <CallResultForm
          v-bind="callResultProps"
          @after:create="handleCallResultCreate"
          @after:update="handleCallResultUpdate"
        />
        <OpportunityForm
          v-bind="opportunityProps"
          @after:create-with-sequence-association="$emit('fetchSequenceItems')"
        />
        <TargetFreeActionForm v-bind="freeActionProps" />
        <SingleNextActionForm v-bind="nextActionProps" />
        <SequenceStepForm
          :call-target="callTarget"
          @after-update="$emit('fetchSequenceItems')"
          @click:call-result-detail="handleCallResultDetailClick"
        />
        <NextActionSuggestionForm
          v-bind="nextActionSuggestionProps"
          @update:next-action-suggestion="handleUpdateNextActionSuggestion"
        />
      </div>
    </BLayout>
  </div>
</template>

<script lang="ts">
import { mapGetters } from 'vuex';
import type { CallResult } from '@/api/openapi';
import MiitelWidget from '@/components/organisms/user/call_target/menu/MiitelWidget.vue';
import TargetFreeActionForm from '@/components/organisms/user/call_target/menu/TargetFreeActionForm.vue';
import ZoomPhoneWidget from '@/components/organisms/user/call_target/menu/ZoomPhoneWidget.vue';
import CallResultForm from '@/components/organisms/user/lead/menu/CallResultForm.vue';
import NextActionSuggestionForm from '@/components/organisms/user/lead/menu/NextActionSuggestionForm.vue';
import OpportunityForm from '@/components/organisms/user/lead/menu/OpportunityForm.vue';
import SequenceStepForm from '@/components/organisms/user/lead/menu/SequenceStepForm.vue';
import SingleNextActionForm from '@/components/organisms/user/lead/menu/SingleNextActionForm.vue';
import { useActiveCti } from '@/composable/user/lead/active-cti';

export default {
  components: {
    MiitelWidget,
    ZoomPhoneWidget,
    CallResultForm,
    OpportunityForm,
    TargetFreeActionForm,
    SingleNextActionForm,
    SequenceStepForm,
    NextActionSuggestionForm,
  },
  props: {
    callTarget: Object,
    callTargetId: Number,
    activeSequenceId: Number,
    miitelCompanyId: String,
    miitelAccessKey: String,
    selectedCallResult: Object,
    selectedNextActionSuggestion: Object,
    users: Array,
  },
  emits: ['fetchSequenceItems', 'fetchNextActionItems', 'fetchHistoryItems', 'clearSelectedSequenceInstance', 'click:callResultDetail', 'updatedDraftItems'],
  setup () {
    const { activeCti } = useActiveCti(true);
    return {
      activeCti,
    };
  },
  data () {
    return {
      recreateState: true,
    };
  },
  computed: {
    ...mapGetters('user', [
      'currentUser',
    ]),
    callResultProps () {
      return {
        callTargetId: this.callTargetId,
        activeSequenceId: this.activeSequenceId,
      };
    },
    opportunityProps () {
      return {
        callTargetId: this.callTargetId,
        activeSequenceId: this.activeSequenceId,
      };
    },
    freeActionProps () {
      return {
        callTargetId: this.callTargetId,
      };
    },
    nextActionProps () {
      return {
        callTargetId: this.callTargetId,
        currentUserId: this.currentUser.id,
      };
    },
    mailProps () {
      return {
        callTargetId: this.callTargetId,
      };
    },
    nextActionSuggestionProps () {
      return {
        nextActionSuggestion: this.selectedNextActionSuggestion,
      };
    },
  },
  watch: {
    miitelCompanyId () {
      this.recreateState = false;
      this.$nextTick(() => {
        this.recreateState = true;
      });
    },
    miitelAccessKey () {
      this.recreateState = false;
      this.$nextTick(() => {
        this.recreateState = true;
      });
    },
  },
  methods: {
    closeWidget () {
      if (this.$refs.miitelWidget?.closeWidget !== undefined) {
        this.$refs.miitelWidget.closeWidget();
      }
    },
    handleCallResultCreate (nextActionCompleting) {
      if (nextActionCompleting) {
        this.$emit('clearSelectedSequenceInstance');
        this.$emit('fetchSequenceItems');
      } else {
        this.$emit('fetchNextActionItems');
      }
    },
    handleCallResultUpdate (nextActionCompleting) {
      if (nextActionCompleting) {
        this.$emit('clearSelectedSequenceInstance');
        this.$emit('fetchSequenceItems');
      }
      this.$emit('fetchHistoryItems');
    },
    handleCallResultDetailClick(callResult: CallResult) {
      this.$emit('click:callResultDetail', callResult);
    },
    handleUpdateNextActionSuggestion () {
      this.$emit('fetchNextActionItems');
      this.$emit('updatedDraftItems');
    },
  },
};
</script>

<style lang="scss" scoped>
.call-target-menu {
  $menu-width: 600px;
  position: absolute;
  top: 0;
  right: -$menu-width;
  z-index: 1;
  width: $menu-width;
  height: 100%;
  &-buttons {
    position: absolute;
    top: 80px;
    left: -60px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  .miitel-contents {
    display: flex;
    flex-direction: column;
    position: relative;
    min-height: 100%;
  }
  .call-target-menu-contents {
    position: relative;
    height: 100%;
    width: 100%;
  }
}
.call-target-menu-layout {
  height: 100%;
}
.menu-header {
  height: $header-height;
  width: 600px;
  border-bottom: 1px solid $bdcolor-light;
  padding-left: 20px;
  .header-title {
    font-size: $fontsize-400;
    font-weight: 600;
  }
}
</style>
